<template>
  <b-card
    :header="$t('fCustomer_info_title')"
    no-body
  >
    <b-list-group flush>
      <b-list-group-item
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="emailClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_email') }}:
        </div>
        <b-input-group
          style="width: 280px"
        >
          <b-input-group-prepend is-text :title="isEmailVerified ? 'Verified' : 'Not verified'">
            <font-awesome-icon v-if="isEmailVerified" :icon="['fas', 'check']" class="text-success" />
            <font-awesome-icon v-else :icon="['fas', 'triangle-exclamation']" class="text-danger" />
          </b-input-group-prepend>
          <b-form-input
            ref="email"
            v-model="email"
            type="text"
            size="sm"
            :disabled="isLocked"
            @input="(value) => onChange('email', value)"
          />
          <b-input-group-append>
            <b-button
              v-if="isEmailVerified"
              variant="outline-danger"
              size="sm"
              style="width: 70px"
              title="Remove verification"
              :disabled="!email || Object.keys(dirty).includes('email')"
              @click.prevent="onUnverifyEmail"
            >
              Remove
            </b-button>
            <b-button
              v-else
              variant="outline-success"
              size="sm"
              style="width: 70px"
              title="Set verified"
              :disabled="!email || Object.keys(dirty).includes('email')"
              @click.prevent="onVerifyEmail"
            >
              Verify
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-list-group-item>
      <b-list-group-item
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="mobileClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_mobile') }}:
        </div>
        <b-input-group
          style="width: 280px"
        >
          <b-input-group-prepend is-text :title="isMobileVerified ? 'Verified' : 'Not verified'">
            <font-awesome-icon v-if="isMobileVerified" :icon="['fas', 'check']" class="text-success" />
            <font-awesome-icon v-else :icon="['fas', 'triangle-exclamation']" class="text-danger" />
          </b-input-group-prepend>
          <b-form-input
            ref="mobile"
            v-model="mobile"
            type="tel"
            size="sm"
            :disabled="isLocked"
            @input="(value) => onChange('mobile', value)"
          />
          <b-input-group-append>
            <b-button
              v-if="isMobileVerified"
              variant="outline-danger"
              size="sm"
              style="width: 70px"
              title="Remove verification"
              :disabled="!mobile || Object.keys(dirty).includes('mobile')"
              @click.prevent="onUnverifyMobile"
            >
              Remove
            </b-button>
            <b-button
              v-else
              variant="outline-success"
              size="sm"
              style="width: 70px"
              title="Set verified"
              :disabled="!mobile || Object.keys(dirty).includes('mobile')"
              @click.prevent="onVerifyMobile"
            >
              Verify
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-list-group-item>
      <b-list-group-item
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="telegramClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_telegram') }}:
        </div>
        <b-input-group
          style="width: 180px"
        >
          <b-form-input
            ref="telegram"
            v-model="telegram"
            type="text"
            size="sm"
            :disabled="isLocked"
            @input="(value) => onChange('telegram', value)"
          />
        </b-input-group>
      </b-list-group-item>
      <b-list-group-item
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="telegramDisabledClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_telegram_disabled') }}:
        </div>

        <b-form-checkbox
          ref="telegram_disabled"
          v-model="telegram_disabled"
          size="sm"
          switch
          :disabled="isLocked"
          @input="(value) => onChange('telegram_disabled', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="passwordClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_password') }}:
        </div>
        <b-form-input
          ref="password"
          v-model="password"
          type="text"
          size="sm"
          style="width: 180px"
          :disabled="isLocked"
          @input="(value) => onChange('password', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="smsChatClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_sms_chat') }}:
        </div>
        <b-form-checkbox
          ref="sms_chat"
          v-model="sms_chat"
          size="sm"
          switch
          :disabled="isLocked"
          @input="(value) => onChange('sms_chat', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="smsMarketingClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_sms_marketing') }}:
        </div>
        <b-form-checkbox
          ref="sms_marketing"
          v-model="sms_marketing"
          size="sm"
          switch
          :disabled="isLocked"
          @input="(value) => onChange('sms_marketing', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="emailNotificationClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_email_notifications') }}:
        </div>
        <b-form-checkbox
          ref="email_notification"
          v-model="email_notification"
          size="sm"
          switch
          :disabled="isLocked"
          @input="(value) => onChange('email_notification', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="cityIdClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_city') }}:
        </div>
        <span
          v-if="city_changed_by_admin"
          class="text-danger small"
        >
          {{ $t('fCustomer_opt_cityChangedByAdmin') }}
        </span>
        <auto-complete
          ref="city_id"
          v-model="city_id"
          type="city"
          :idx="idx"
          size="sm"
          style="width: 180px"
          :disabled="isLocked"
          placeholder="Select city"
          @input="(value) => onChange('city_id', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="bdayClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_birthDate') }}:
        </div>
        <b-input-group
          style="width: 180px"
        >
          <b-form-input
            ref="bday"
            v-model="bdayDisplay"
            type="text"
            size="sm"
            :disabled="isLocked"
            :state="bdayState"
            :formatter="bdayFormatter"
            @input="(value) => onChange('bday', value)"
          />
          <b-input-group-append>
            <b-form-datepicker
              v-model="bday"
              type="date"
              size="sm"
              style="width: 40px"
              :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
              :disabled="isLocked"
              locale="en"
              close-button
              button-variant="outline-secondary"
              button-only
              right
              @context="onBdayContext"
            />
          </b-input-group-append>
        </b-input-group>
      </b-list-group-item>
      <b-list-group-item
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="tagsClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_tags') }}:
        </div>
        <auto-complete
          ref="tags"
          v-model="tags"
          type="tag"
          :idx="idx"
          size="sm"
          style="max-width: 300px"
          :disabled="isLocked"
          :placeholder="$t('fCustomer_info_tags_placeholder')"
          :label-not-found="$t('fCustomer_info_no_results')"
          multiple
          @input="(value) => onChange('tags', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        v-if="type === 'hostess'"
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="servicesClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_services') }}:
        </div>
        <auto-complete
          ref="services"
          v-model="services"
          type="service"
          :idx="idx"
          size="sm"
          style="max-width: 300px"
          :disabled="isLocked"
          :placeholder="$t('fCustomer_info_services_placeholder')"
          :label-not-found="$t('fCustomer_info_no_results')"
          multiple
          @input="(value) => onChange('services', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        v-if="type === 'hostess'"
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="nationalityClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_nationality') }}:
        </div>
        <auto-complete
          ref="characterHostessNationality"
          v-model="characterHostessNationality"
          type="nationality"
          :idx="idx"
          size="sm"
          style="max-width: 300px"
          :disabled="isLocked"
          :placeholder="$t('fCustomer_info_nationality_placeholder')"
          :label-not-found="$t('fCustomer_info_no_results')"
          multiple
          @input="(value) => onChange('characterHostessNationality', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        v-if="type === 'hostess'"
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="languagesClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_languages') }}:
        </div>
        <auto-complete
          ref="characterSpokenLanguages"
          v-model="characterSpokenLanguages"
          type="language"
          :idx="idx"
          size="sm"
          style="max-width: 300px"
          :disabled="isLocked"
          :placeholder="$t('fCustomer_info_languages_placeholder')"
          :label-not-found="$t('fCustomer_info_no_results')"
          multiple
          @input="(value) => onChange('characterSpokenLanguages', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        v-if="type === 'hostess'"
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="heightClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_height') }}:
        </div>
        <b-form-input
          ref="characterHeight"
          v-model="characterHeight"
          type="text"
          size="sm"
          style="width: 180px"
          :disabled="isLocked"
          @input="(value) => onChange('characterHeight', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        v-if="type === 'hostess'"
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="weightClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_weight') }}:
        </div>
        <b-form-input
          ref="characterWeight"
          v-model="characterWeight"
          type="text"
          size="sm"
          style="width: 180px"
          :disabled="isLocked"
          @input="(value) => onChange('characterWeight', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        v-if="type === 'hostess'"
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="sizeClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_size') }}:
        </div>
        <b-form-input
          ref="characterSize"
          v-model="characterSize"
          type="text"
          size="sm"
          style="width: 180px"
          :disabled="isLocked"
          @input="(value) => onChange('characterSize', value)"
        />
      </b-list-group-item>
      <b-list-group-item
        v-if="type === 'hostess'"
        class="d-flex flex-row flex-nowrap align-items-center justify-content-between px-2"
        :class="transferClass"
      >
        <div class="small text-nowrap">
          {{ $t('fCustomer_info_transfer') }}:
        </div>
        <b-form-checkbox
          ref="characterAvailableForTransfer"
          v-model="characterAvailableForTransfer"
          size="sm"
          switch
          :disabled="isLocked"
          @input="(value) => onChange('characterAvailableForTransfer', value)"
        />
      </b-list-group-item>
    </b-list-group>
  </b-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import { mapComputedFormFields, mapComputedStateFields } from '~/mixins/globalForm'

export default {
  name: 'InfoList',
  props: {
    itemId: {
      type: [Number, String],
      required: true
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    repo: {
      type: String,
      required: true,
      default: ''
    },
    idx: {
      type: Number,
      default: -1
    },
    isLocked: {
      type: Boolean,
      default: false
    },
    uniqKey: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      bdayDisplay: moment(this.bday).format('DD.MM.YYYY'),
      bdayState: true,
      // TODO: move that at column's config (ie. `trackCursor: true`)
      trackCursor: [
        'email',
        'mobile',
        'telegram',
        'password',
        'bday',
        'characterHeight',
        'characterWeight',
        'characterSize',
        'city_id',
        'tags'
      ]
    }
  },
  computed: {
    ...mapGetters({
      getByName: 'repos/getByName'
    }),
    ...mapComputedFormFields('customerEdit', [
      'email',
      'mobile',
      'telegram',
      'telegram_disabled',
      'password',
      'sms_chat',
      'sms_marketing',
      'email_notification',
      'city_id',
      'city_changed_by_admin',
      'bday',
      'tags',
      'services',
      'characterHostessNationality',
      'characterSpokenLanguages',
      'characterHeight',
      'characterWeight',
      'characterSize',
      'characterAvailableForTransfer'
    ]),
    ...mapComputedStateFields('customerEdit', [
      'info',
      'dirty',
      'type',
      'editColumnName',
      'editColumnCursor',
      'editScrollPosition'
    ]),
    isEmailVerified () {
      return this.info.profile.email_verified !== null && !!this.info.profile.email_verified
    },
    isMobileVerified () {
      return this.info.profile.mobile_verified !== null && !!this.info.profile.mobile_verified
    },
    emailClass () {
      if (this.isDirty('email')) {
        return 'bg-warning'
      }
      return this.email && this.email !== '' ? 'bg-success' : 'bg-danger'
    },
    mobileClass () {
      if (this.isDirty('mobile')) {
        return 'bg-warning'
      }
      return this.mobile && this.mobile !== '' ? 'bg-success' : 'bg-danger'
    },
    telegramClass () {
      if (this.isDirty('telegram')) {
        return 'bg-warning'
      }
      return this.telegram && this.telegram !== '' ? 'bg-success' : 'bg-danger'
    },
    telegramDisabledClass () {
      if (this.isDirty('telegram_disabled')) {
        return 'bg-warning'
      }
      return this.telegram_disabled && this.telegram_disabled !== '' ? 'bg-success' : 'bg-danger'
    },
    passwordClass () {
      if (this.isDirty('password')) {
        return 'bg-warning'
      }
      return this.password && this.password !== '' ? 'bg-warning' : ''
    },
    smsChatClass () {
      if (this.isDirty('sms_chat')) {
        return 'bg-warning'
      }
      return this.sms_chat && this.sms_chat !== '' ? 'bg-success' : 'bg-danger'
    },
    smsMarketingClass () {
      if (this.isDirty('sms_marketing')) {
        return 'bg-warning'
      }
      return this.sms_marketing && this.sms_marketing !== '' ? 'bg-success' : 'bg-danger'
    },
    emailNotificationClass () {
      if (this.isDirty('email_notification')) {
        return 'bg-warning'
      }
      return this.email_notification && this.email_notification !== '' ? 'bg-success' : 'bg-danger'
    },
    cityIdClass () {
      if (this.isDirty('city_id')) {
        return 'bg-warning'
      }
      return this.city_id && this.city_id !== '' ? 'bg-success' : 'bg-danger'
    },
    bdayClass () {
      if (this.isDirty('bday')) {
        return 'bg-warning'
      }
      return this.bday && this.bday !== '' ? 'bg-success' : 'bg-danger'
    },
    tagsClass () {
      if (this.isDirty('tags')) {
        return 'bg-warning'
      }
      return this.tags && this.tags.length ? 'bg-success' : 'bg-danger'
    },
    servicesClass () {
      if (this.isDirty('services')) {
        return 'bg-warning'
      }
      return this.services && this.services.length ? 'bg-success' : 'bg-danger'
    },
    nationalityClass () {
      if (this.isDirty('characterHostessNationality')) {
        return 'bg-warning'
      }
      return this.characterHostessNationality && this.characterHostessNationality.length ? 'bg-success' : 'bg-danger'
    },
    languagesClass () {
      if (this.isDirty('characterSpokenLanguages')) {
        return 'bg-warning'
      }
      return this.characterSpokenLanguages && this.characterSpokenLanguages.length ? 'bg-success' : 'bg-danger'
    },
    heightClass () {
      if (this.isDirty('characterHeight')) {
        return 'bg-warning'
      }
      return this.characterHeight && this.characterHeight !== '' ? 'bg-success' : 'bg-danger'
    },
    weightClass () {
      if (this.isDirty('characterWeight')) {
        return 'bg-warning'
      }
      return this.characterWeight && this.characterWeight !== '' ? 'bg-success' : 'bg-danger'
    },
    sizeClass () {
      if (this.isDirty('characterSize')) {
        return 'bg-warning'
      }
      return this.characterSize && this.characterSize !== '' ? 'bg-success' : 'bg-danger'
    },
    transferClass () {
      if (this.isDirty('characterAvailableForTransfer')) {
        return 'bg-warning'
      }
      return this.characterAvailableForTransfer && this.characterAvailableForTransfer !== '' ? 'bg-success' : 'bg-danger'
    }
  },
  watch: {},
  mounted () {
    this.$nextTick(() => {
      this.setInputState()
    })
  },
  methods: {
    ...mapActions({
      verifyEmail: 'forms/customerEdit/verifyEmail',
      unverifyEmail: 'forms/customerEdit/unverifyEmail',
      verifyMobile: 'forms/customerEdit/verifyMobile',
      unverifyMobile: 'forms/customerEdit/unverifyMobile'
    }),
    setInputState () {
      const isInRefs = Object.keys(this.$refs).includes(this.editColumnName)
      const isRefExists = !!this.$refs[this.editColumnName]
      const isInViewport = (el) => {
        if (typeof el === 'undefined') {
          return false
        }
        const rect = el.getBoundingClientRect()
        const result = rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        return result
      }

      if (this.editScrollPosition > 0) {
        document.querySelector('.customer-form-wrapper')?.scrollTo({ top: this.editScrollPosition })
      }
      if (!isInRefs || !isRefExists) {
        this.editColumnName = undefined
        return
      }
      const el = this.$refs[this.editColumnName].$el
      if (el && this.$refs[this.editColumnName].$el.setSelectionRange && this.editColumnCursor >= 0) {
        this.$nextTick(() => {
          el.focus()
          if (!isInViewport(el)) {
            el.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' })
          }
          this.$nextTick(() => {
            el.setSelectionRange(this.editColumnCursor, this.editColumnCursor)
          })
        })
      } else if (el) {
        // search input inside wrapper
        const input = el.querySelector('input, select')
        if (input) {
          input.focus()
          if (!isInViewport(input)) {
            el.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' })
          }
        } else {
          el.focus()
          if (!isInViewport(el)) {
            el.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' })
          }
        }
      }
    },
    onChange (colName, value) {
      this.editColumnName = colName
      const column = this.$refs[colName]?.$el
      const type = column?.type || ''
      const acceptableTypes = ['text', 'search', 'url', 'tel', 'password']
      if (column && acceptableTypes.includes(type.toLowerCase())) {
        this.editColumnCursor = column.selectionStart || -1
      } else {
        this.editColumnCursor = -1
      }
      this.editScrollPosition = document.querySelector('.customer-form-wrapper')?.scrollTop || 0
      this.setInputState()
    },
    isDirty (fieldKey) {
      const dirtyFields = this.dirty && Object.keys(this.dirty)
      if (dirtyFields && dirtyFields.length && dirtyFields.includes(fieldKey)) {
        return true
      }
      return false
    },
    async onVerifyEmail () {
      await this.verifyEmail({ itemId: this.itemId, value: this.email })
      this.$emit('refresh')
    },
    async onUnverifyEmail () {
      await this.unverifyEmail({ itemId: this.itemId, value: this.email })
      this.$emit('refresh')
    },
    async onVerifyMobile () {
      await this.verifyMobile(this.itemId)
      this.$emit('refresh')
    },
    async onUnverifyMobile () {
      await this.unverifyMobile(this.itemId)
      this.$emit('refresh')
    },
    bdayFormatter (v) {
      const parsed = moment(v, 'DD.MM.YYYY')
      if (parsed.isValid() && parsed.format('DD.MM.YYYY') === v) {
        if (this.bday !== parsed.format('YYYY-MM-DD')) {
          this.bday = parsed.format('YYYY-MM-DD')
        }
        this.bdayState = true
      } else {
        this.bdayState = false
      }
      return v
    },
    onBdayContext (ctx) {
      this.bday = ctx.selectedYMD
      this.bdayDisplay = moment(ctx.selectedYMD).format('DD.MM.YYYY')
    }
  }
}
</script>

<style scoped>

</style>
